import type { RoleAssignment } from './schemas/user.js';

// #23 Tenant-Isolation. DIE EINE Stelle, an der aus der projektübergreifenden Rollenmenge
// (`GET /v1/auth/me` bzw. die Zeilen aus `user_roles`) die Sicht auf GENAU EIN Projekt wird.
//
// Bewusst in @testflow/shared und nicht doppelt: Server (`middleware/project-scope.ts`,
// `services/notifications/dispatch.ts`) und Client (`apps/web/src/lib/project-roles.ts`) müssen
// hier wörtlich dasselbe tun. Als die Regel noch zweimal dastand, war sie bereits auseinander
// gelaufen — die Glocke scopte den Empfänger, die Mail nicht, und derselbe `defect_assigned`
// führte in der Mail auf `/defects/:id` und in der Glocke auf `/my-tests` (N3). Und der Server
// normalisierte nur die URL-Seite der UUID, der Client beide (N6).

/**
 * Hält der Nutzer die Rolle `admin` in IRGENDEINEM Projekt? Das ist die Plattform-Betreiberrolle:
 * Sie begründet die Ausnahme in `scopeRolesToProject` (Punkt 2 unten) UND die Projektliste in
 * `apps/api/src/routes/projects.ts` — wer sie hält, sieht alle Projekte.
 *
 * Bewusst ein benannter Export und keine zweite `roles.some(r => r.role === 'admin')`-Zeile:
 * Solange die Ausnahme nur in `scopeRolesToProject` stand und die Liste stur auf explizite
 * `project_id`-Zeilen filterte, trat genau der Lock-out ein, den die Ausnahme verhindern sollte —
 * ein neu angelegtes Projekt tauchte in der Liste des Betreibers nie auf, und die Ausnahme wirkte
 * nur für den, der die Projekt-UUID von Hand kannte (Review-Fund I1).
 */
export function isGlobalAdmin(roles: readonly RoleAssignment[]): boolean {
  return roles.some((r) => r.role === 'admin');
}

/**
 * Beschneidet Rollenzuweisungen auf GENAU EIN Projekt.
 *
 * 1. Rollen dieses Projekts gewinnen.
 * 2. Wer hier keine Rolle hat, aber `admin` in IRGENDEINEM Projekt hält, gilt als Admin dieses
 *    Projekts (Plattform-Betreiberrolle — sonst sperrt sich der Betreiber aus jedem neu
 *    angelegten Projekt aus). Wer hier eine kleinere Rolle hat, behält sie: eine „Beförderung"
 *    allein für den, der anderswo Admin ist, wäre genau die Inkonsistenz, die #23 abstellt.
 * 3. Ohne Projekt bleibt keine Rolle übrig (fail-closed).
 *
 * UUID-Vergleich case-insensitiv auf BEIDEN Seiten: Postgres gibt `uuid`-Spalten zwar kanonisch
 * klein aus, aber der Vergleich hier ist ein String-Vergleich — er darf sich nicht darauf
 * verlassen, woher der Wert stammt.
 */
export function scopeRolesToProject(
  roles: readonly RoleAssignment[],
  projectId: string | null | undefined,
): RoleAssignment[] {
  if (!projectId) return [];
  const wanted = projectId.toLowerCase();

  const own = roles.filter((r) => r.project_id?.toLowerCase() === wanted);
  if (own.length > 0) return [...own];

  if (!isGlobalAdmin(roles)) return [];
  return [{ role: 'admin', scope_node_id: null, project_id: wanted }];
}
